'use client'

import dynamic from 'next/dynamic'
import { ArrowUpRight, Percent, TrendingUp, Wallet } from 'lucide-react'
import type { CaseRecord, Holding, PrescriptionStep } from '@/features/cases'
import { formatCurrency, formatHeadlineCurrency } from '@/lib/format/currency'
import { GrowthPlanCard } from '@/features/investor/components/home/growth-plan-card'
import { MetricGrid } from '@/features/investor/components/home/metric-grid'
import { WealthScoreCard } from '@/features/investor/components/home/wealth-score-card'

/**
 * Loaded on demand: `echarts` (pulled in by `PortfolioGrowthChart`) is a large canvas charting
 * library only this hero needs. `investor-dashboard-view.tsx` is re-exported from the
 * `features/investor` barrel, which `/profile` also imports from — a static import here would
 * leak `echarts` into every route sharing that barrel (the same bug already fixed once for Admin
 * Overview; see PROGRESS.md). `next/dynamic`'s `import()` is a real async chunk boundary
 * regardless of the barrel's static structure.
 */
const PortfolioGrowthChart = dynamic(
  () =>
    import('@/features/investor/components/home/portfolio-growth-chart').then(
      (m) => m.PortfolioGrowthChart,
    ),
  { ssr: false },
)

/** audit → wealth score + portfolio value, the only service with a chart-worthy history.
 * Ported from `DashboardHome.tsx`'s `AuditHero`. Real, pre-fetched data — this component's sole
 * caller (`investor-dashboard-view.tsx`) always supplies both props. */
export function AuditHero({
  caseRecord,
  injectedHoldings,
  injectedSteps,
  onGrowthPlanStepsClick,
}: {
  caseRecord: CaseRecord
  injectedHoldings?: Holding[]
  injectedSteps?: PrescriptionStep[]
  /** Forwarded to `GrowthPlanCard` — see its own doc comment. */
  onGrowthPlanStepsClick?: () => void
}) {
  const holdings = injectedHoldings ?? []
  const prescriptionSteps = injectedSteps ?? []

  const invested = holdings.reduce((s, h) => s + h.investedValue, 0)
  const current = holdings.reduce((s, h) => s + h.currentValue, 0)
  const gain = current - invested
  const gainPct = invested ? (gain / invested) * 100 : 0
  const weightedXirr = current
    ? holdings.reduce((s, h) => s + h.xirr * h.currentValue, 0) / current
    : 0

  return (
    <div className="flex flex-col gap-4">
      <MetricGrid
        metrics={[
          {
            icon: <Wallet />,
            tint: 'var(--chart-2)',
            label: 'Invested Value',
            value: formatHeadlineCurrency(invested),
            title: formatCurrency(invested),
            footnote: "Total amount you've put in",
          },
          {
            icon: <TrendingUp />,
            tint: 'var(--chart-1)',
            label: 'Current Value',
            value: formatHeadlineCurrency(current),
            title: formatCurrency(current),
            trend: gainPct !== 0 ? `${Math.abs(gainPct).toFixed(1)}%` : undefined,
            trendUp: gain > 0,
            footnote: "What it's worth today",
          },
          {
            icon: <ArrowUpRight />,
            tint: 'var(--chart-positive)',
            label: 'Total Gain / Loss',
            value: `${gain >= 0 ? '+' : '−'}${formatHeadlineCurrency(Math.abs(gain))}`,
            title: `${gain >= 0 ? '+' : '−'}${formatCurrency(Math.abs(gain))}`,
            trend: gainPct !== 0 ? `${Math.abs(gainPct).toFixed(1)}%` : undefined,
            trendUp: gain > 0,
            footnote: 'vs. invested',
          },
          {
            icon: <Percent />,
            tint: 'var(--chart-4)',
            label: 'XIRR',
            value: `${weightedXirr.toFixed(1)}%`,
            footnote: 'Annualised return (value-weighted)',
          },
        ]}
      />
      <div className="@4xl/main:grid-cols-[minmax(300px,320px)_minmax(320px,1fr)_minmax(300px,330px)] grid grid-cols-1 items-stretch gap-4">
        <WealthScoreCard score={caseRecord.wealthScore} />
        <PortfolioGrowthChart />
        <GrowthPlanCard
          score={caseRecord.wealthScore}
          steps={prescriptionSteps}
          onStepsClick={onGrowthPlanStepsClick}
        />
      </div>
    </div>
  )
}
